import { makeStyles } from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import React, { FunctionComponent, useState } from 'react';

import { Colors } from 'src/common/ui/base/Colors';
import IconInBox from 'src/common/ui/components/IconInBox/IconInBox';
import { useMyDayTodayCurrentStatus } from 'src/myDayToday/hooks/useMyDayTodayCurrentStatus';

export type CurrentStatusSectionProps = {
  providerId: string;
};

type StatConfig = {
  icon: React.ReactNode;
  label: string;
  value: number | null;
  caption: string;
};

export const CurrentStatusSection: FunctionComponent<CurrentStatusSectionProps> = ({
  providerId,
}) => {
  const styles = useStyles();
  const { currentStatus } = useMyDayTodayCurrentStatus(providerId);
  const [isExpanded, setIsExpanded] = useState(true);

  const stats: StatConfig[] = [
    // Task functionality is not being launched yet.
    // https://bouldercare.atlassian.net/browse/ORCA-2479
    // {
    //   icon: <IconInBox.Checkbox />,
    //   label: 'Tasks completed',
    //   value: currentStatus?.tasksCompleted ?? null,
    //   caption: 'Great work!',
    // },
    {
      icon: <IconInBox.Star />,
      label: 'Visits completed',
      value: currentStatus?.visitsCompleted ?? null,
      caption: 'Great work!',
    },
    {
      icon: <IconInBox.Clock />,
      label: 'Average wait time',
      value: currentStatus?.avgWaitTime ?? null,
      caption: 'minutes',
    },
    {
      icon: <IconInBox.Stethoscope />,
      label: 'Clinic volume',
      value: currentStatus?.clinicVolume ?? null,
      caption: currentStatus?.clinicVolumeLabel ?? '',
    },
    {
      icon: <IconInBox.People />,
      label: 'Providers working',
      value: currentStatus?.providersWorking ?? null,
      caption: 'online right now',
    },
  ];

  return (
    <div className={styles.card}>
      <button
        type="button"
        className={styles.header}
        onClick={() => setIsExpanded(prev => !prev)}
        aria-expanded={isExpanded}
        aria-controls="current-status-stats"
      >
        <div className={styles.headerLeft}>
          <IconInBox>
            <IconInBox.Equalizer />
          </IconInBox>
          <span className={styles.title}>Current status</span>
        </div>
        {isExpanded ? (
          <ExpandLess aria-hidden="true" className={styles.expandIcon} />
        ) : (
          <ExpandMore aria-hidden="true" className={styles.expandIcon} />
        )}
      </button>
      {isExpanded && (
        <div id="current-status-stats" className={styles.statsRow}>
          {stats.map(({ icon, label, value, caption }, index) => (
            <div key={label} className={index < stats.length - 1 ? styles.stat : styles.statLast}>
              <IconInBox>{icon}</IconInBox>
              <div className={styles.statText}>
                <span className={styles.overline}>{label}</span>
                <span className={styles.number}>{value ?? '—'}</span>
                {value !== null && caption && <span className={styles.caption}>{caption}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const useStyles = makeStyles({
  card: {
    width: '100%',
    backgroundColor: Colors.White,
    border: `1px solid ${Colors.CoolGray2}`,
    borderRadius: 12,
    boxShadow: '0px 2px 1px rgba(0, 0, 0, 0.15)',
    fontFamily: '"Nunito", "Nunito Sans"',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    width: '100%',
    background: 'none',
    border: 'none',
    borderBottom: `1px solid ${Colors.CoolGray2}`,
    cursor: 'pointer',
    borderRadius: 0,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontWeight: 600,
    fontSize: 16,
    color: Colors.CoolGray8,
  },
  statsRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
  },
  stat: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: 180,
  },
  statLast: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  overline: {
    fontWeight: 600,
    fontSize: 10,
    color: Colors.CoolGray9,
    textTransform: 'uppercase',
    letterSpacing: '1.2px',
  },
  number: {
    fontWeight: 700,
    fontSize: 14,
    color: Colors.CoolGray8,
    letterSpacing: '1.68px',
  },
  caption: {
    fontWeight: 600,
    fontSize: 12,
    color: Colors.CoolGray7,
  },
  expandIcon: {
    width: 32,
    height: 32,
    color: Colors.CoolGray8,
  },
});
